/**
 * InvoiceCapture — file-input + drag/drop zone for an invoice document.
 *
 * Accepts: image/jpeg, image/png, image/webp, image/heic, application/pdf.
 * Max 25 MiB validated client-side (backend also enforces).
 *
 * Accessibility and UX follows the DataPlateCapture pattern:
 *   - Drag zone is keyboard-activatable (Enter/Space opens file picker).
 *   - Hidden <input> is aria-hidden; visible zone has role="button".
 *   - Mobile: no `capture` attribute, so the native chooser offers both camera
 *     and photo library / files (capture="environment" would force the camera
 *     and hide the library — invoices are usually existing photos or PDFs).
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import type { CSSProperties } from 'react';
import { useCallback, useRef, useState } from 'react';

interface InvoiceCaptureProps {
  onFile: (file: File) => void;
  disabled?: boolean;
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,application/pdf';
const MAX_BYTES = 25 * 1024 * 1024; // 25 MiB

function isAllowedType(type: string): boolean {
  return ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'].includes(type);
}

function isDragEvent(e: React.DragEvent): boolean {
  return e.dataTransfer.types.includes('Files');
}

export function InvoiceCapture({ onFile, disabled = false }: InvoiceCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleFile = useCallback(
    (file: File) => {
      setValidationError(null);
      if (!isAllowedType(file.type)) {
        setValidationError('Please upload a JPEG, PNG, WEBP, HEIC, or PDF file.');
        return;
      }
      if (file.size > MAX_BYTES) {
        setValidationError('File is too large. Maximum size is 25 MB.');
        return;
      }
      onFile(file);
    },
    [onFile],
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  function handleDragOver(e: React.DragEvent) {
    if (!isDragEvent(e) || disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave() {
    setIsDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }

  const zoneStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.sm,
    padding: `${SPACE.lg}px ${SPACE.md}px`,
    border: `2px dashed ${isDragOver ? T.accent : T.border}`,
    borderRadius: RADIUS.lg,
    background: isDragOver ? T.cardAlt : 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: `background ${TRANSITION.fast}, border-color ${TRANSITION.fast}`,
    outline: 'none',
    opacity: disabled ? 0.6 : 1,
    minHeight: 140,
  };

  const iconStyle: CSSProperties = {
    width: 40,
    height: 40,
    color: T.textMuted,
    display: 'block',
  };

  return (
    <>
      {/* No `capture` attribute: the mobile chooser offers both camera and
          photo library / files (capture would force the camera, hiding both —
          invoices are usually existing photos or PDFs). */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        aria-hidden="true"
        tabIndex={-1}
        style={{ display: 'none' }}
        onChange={handleChange}
        disabled={disabled}
      />

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Take or upload a photo of your invoice or parts order"
        aria-disabled={disabled}
        style={zoneStyle}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
      >
        {/* Receipt/document icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={iconStyle}
          aria-hidden="true"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>

        <div style={{ textAlign: 'center' }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 600,
              color: T.text,
              fontFamily: F.body,
            }}
          >
            {isDragOver ? 'Drop to upload' : 'Add invoice or receipt'}
          </p>
          <p
            style={{
              margin: `${SPACE.xs}px 0 0`,
              fontSize: 12,
              color: T.textMuted,
              fontFamily: F.body,
            }}
          >
            Photo, scan, or PDF · up to 25 MB
          </p>
        </div>
      </div>

      {validationError ? (
        <p role="alert" style={{ color: T.red, fontSize: 12, margin: `${SPACE.sm}px 0 0` }}>
          {validationError}
        </p>
      ) : null}
    </>
  );
}
